// Date utility functions
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) return ''

  const day = dateObj.getDate().toString().padStart(2, '0')
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
  const year = dateObj.getFullYear()

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    case 'DD MMM YYYY':
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[dateObj.getMonth()]} ${year}`
    case 'MMM DD, YYYY':
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${monthNames[dateObj.getMonth()]} ${day}, ${year}`
    case 'MMMM DD, YYYY':
      // Format: "January 13, 2026" (long month name)
      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    default:
      return `${day}/${month}/${year}`
  }
}

/**
 * Format date for "as on" display with long month format
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Formatted date string like "January 13, 2026" or "--" if invalid
 */
export const formatAsOnDate = (date) => {
  if (!date) return "--";

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) return "--";

  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format date in NavRangeCard format: "Day Mon DD YYYY" (e.g., "Fri Nov 13 2025")
 * @param {string|Date} date - Date string or Date object
 * @returns {string|null} Formatted date string or null if invalid
 */
export const formatAsOnDateNavRange = (date) => {
  if (!date || date === '-') return null;

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[dateObj.getDay()];
    const monthName = months[dateObj.getMonth()];
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();

    return `${dayName} ${monthName} ${day} ${year}`;
  } catch (error) {
    return null;
  }
}

export const getRelativeTime = (date) => {
  if (!date) return ''

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffTime = Math.abs(now - dateObj)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 30) {
    return `${diffDays} days ago`
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30)
    return `${months} month${months > 1 ? 's' : ''} ago`
  } else {
    const years = Math.floor(diffDays / 365)
    return `${years} year${years > 1 ? 's' : ''} ago`
  }
}

export const getHoldingPeriod = (startDate) => {
  if (!startDate) return ''

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const now = new Date()
  const diffTime = Math.abs(now - start)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 30) {
    return `${diffDays} days`
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30)
    return `${months} month${months > 1 ? 's' : ''}`
  } else {
    const years = Math.floor(diffDays / 365)
    const remainingMonths = Math.floor((diffDays % 365) / 30)
    if (remainingMonths > 0) {
      return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`
    }
    return `${years} year${years > 1 ? 's' : ''}`
  }
}

/**
 * Calculate age from date of birth
 * @param {string|Date} dateOfBirth - Date of birth string or Date object
 * @returns {number|null} Age in years, or null if invalid date
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null

  let dob
  if (typeof dateOfBirth === 'string') {
    // Parse YYYY-MM-DD format correctly to avoid timezone issues
    if (dateOfBirth.includes('-')) {
      const [year, month, day] = dateOfBirth.split('-').map(Number)
      dob = new Date(year, month - 1, day)
    } else {
      dob = new Date(dateOfBirth)
    }
  } else {
    dob = dateOfBirth
  }

  // Check if date is valid
  if (isNaN(dob.getTime())) return null

  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()

  // Adjust age if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }

  // Return null for future dates (invalid)
  if (age < 0) return null

  return age
}

/**
 * Parse date string from "DD MMM YYYY" format to "YYYY-MM-DD" format
 * @param {string} dateString - Date string in format "DD MMM YYYY" (e.g., "18 Nov 2025")
 * @returns {string} Date string in format "YYYY-MM-DD" (e.g., "2025-11-18") or empty string if invalid
 */
export const parseDateToISO = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return ''

  // Parse format: "DD MMM YYYY" (e.g., "18 Nov 2025")
  const match = dateString.match(/(\d+)\s+(\w+)\s+(\d+)/i)
  if (!match) return ''

  const day = parseInt(match[1], 10)
  const monthStr = match[2].toLowerCase().substring(0, 3)
  const year = parseInt(match[3], 10)

  // Map month abbreviations to month numbers (0-indexed)
  const monthMap = {
    'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
    'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
  }

  const month = monthMap[monthStr]
  if (month === undefined) return ''

  // Validate date
  const date = new Date(year, month, day)
  if (isNaN(date.getTime())) return ''

  // Check if the date matches the input (handles invalid dates like Feb 30)
  if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
    return ''
  }

  // Format as YYYY-MM-DD
  const formattedMonth = (month + 1).toString().padStart(2, '0')
  const formattedDay = day.toString().padStart(2, '0')

  return `${year}-${formattedMonth}-${formattedDay}`
}

/**
 * Calculate years from inception date to today
 * @param {string|Date} inceptionDate - Inception date string or Date object
 * @returns {number|null} Years from inception to today, or null if invalid date
 */
export const calculateYearsFromInception = (inceptionDate) => {
  if (!inceptionDate) return null

  try {
    const inception = typeof inceptionDate === 'string' ? new Date(inceptionDate) : inceptionDate

    if (isNaN(inception.getTime())) return null

    const today = new Date()
    let years = today.getFullYear() - inception.getFullYear()
    const monthDiff = today.getMonth() - inception.getMonth()

    // Adjust years if the anniversary hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < inception.getDate())) {
      years--
    }

    // Return null for future dates (invalid)
    if (years < 0) return null

    return years
  } catch (error) {
    return null
  }
}

/**
 * Generate time range tabs specifically for Rolling Alpha chart based on inception date
 * Required tabs: 3M, 6M, 1Y. Optional: 3Y (if age >= 3), 5Y (if age >= 5)
 * @param {string|Date} inceptionDate - Date fund was started
 * @returns {string[]} Array of available time tabs (e.g. ['3M', '6M', '1Y', '3Y', '5Y'])
 */
export const getRollingAlphaTabs = (inceptionDate) => {
  const yearsFromInception = calculateYearsFromInception(inceptionDate);

  // If no inception date or invalid, show default tabs
  if (yearsFromInception === null) {
    return ['3M', '6M', '1Y', '3Y', '5Y'];
  }

  // Always include fixed short-term tabs
  const filteredTabs = ['3M', '6M', '1Y'];

  // Add year tabs based on fund age
  if (yearsFromInception >= 3) filteredTabs.push('3Y');
  if (yearsFromInception >= 5) filteredTabs.push('5Y');

  return filteredTabs;
};

/**
 * Get local date as string in YYYY-MM-DD format
 * @param {string|Date} date - Date object or string (defaults to current)
 * @returns {string} Formatted date like "2026-03-24"
 */
export const getLocalDateStr = (date = new Date()) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};